import { http } from '@/api/http'
import type {
  ContentLocale,
  ProcurementReportCatalog,
  ProcurementReportFilterOptions,
  ProcurementReportFilters,
  ProcurementReportPage,
  ProcurementPlanner,
} from '@/types/api'
import { sheetTable, type ExportHeader, type ExportSheetDto } from '@/lib/tableExport'
import type { RequestOptions } from '../crud'

const base = '/procurement/reports'

/** Report labels use uz_cyrl where the admin content locale is cyr. */
/** The server's language key for a content locale; procurement exports use it too. */
export const reportLanguage = (locale: ContentLocale) => (locale === 'cyr' ? 'uz_cyrl' : locale)

export type ProcurementReportQuery = {
  locale: ContentLocale
  filters: ProcurementReportFilters
  page: number
  limit: number
}

export const procurementReportsService = {
  async catalog(locale: ContentLocale, options?: RequestOptions) {
    const { data } = await http.get<ProcurementReportCatalog>(base, { params: { lang: reportLanguage(locale) }, ...options })
    return data.reports ?? []
  },
  async report(key: string, query: ProcurementReportQuery, options?: RequestOptions) {
    const { data } = await http.get<ProcurementReportPage>(`${base}/${encodeURIComponent(key)}`, {
      params: {
        lang: reportLanguage(query.locale),
        filters: JSON.stringify(query.filters),
        page: query.page,
        limit: query.limit,
      },
      ...options,
    })
    return data
  },
  async filterOptions(key: string, filter: string, search: string, locale: ContentLocale, options?: RequestOptions) {
    const { data } = await http.get<ProcurementReportFilterOptions>(`${base}/${encodeURIComponent(key)}/filter-options`, {
      params: { filter, search, lang: reportLanguage(locale) },
      ...options,
    })
    return data.options ?? []
  },
  /**
   * The «Календарь» report: payables of a period of at most 93 days. An API
   * from before the calendar still sends «accrued» items (on their accrual
   * date); they read as completed planned ones, and only the two known state
   * totals are kept, so the view never meets a state it cannot colour.
   */
  async planner(from: string, to: string, filters: ProcurementReportFilters, options?: RequestOptions) {
    const { data } = await http.get<ProcurementPlanner>('/procurement/planner', {
      params: { from, to, filters: JSON.stringify(filters) },
      ...options,
    })
    const known = (state: string): state is ProcurementPlanner['items'][number]['state'] => state === 'overdue' || state === 'planned'
    return {
      ...data,
      items: data.items.map((item) =>
        known(item.state) ? { ...item, completed: Boolean(item.completed) } : { ...item, state: 'planned' as const, completed: true, actual_date: item.actual_date ?? item.date },
      ),
      state_totals: data.state_totals.filter((total) => known(total.state)),
    }
  },
  /**
   * The report in the export standard (Среда, Отчёт создан, Исполнитель, the
   * filters, then the table): an Excel or a PDF file of every row.
   */
  async export(key: string, locale: ContentLocale, filters: ProcurementReportFilters, format: 'xlsx' | 'pdf' = 'xlsx', header?: ExportHeader) {
    const { data } = await http.post<Blob>(
      `${base}/${encodeURIComponent(key)}/export`,
      { lang: reportLanguage(locale), filters, format, company: header?.company, author: header?.author },
      { responseType: 'blob' },
    )
    return data
  },
  /** The same sheet as JSON, for «Печать» and «Открыть в браузере». */
  async exportSheet(key: string, locale: ContentLocale, filters: ProcurementReportFilters, header: ExportHeader) {
    const { data } = await http.post<ExportSheetDto>(`${base}/${encodeURIComponent(key)}/export`, {
      lang: reportLanguage(locale),
      filters,
      format: 'sheet',
      company: header.company,
      author: header.author,
    })
    return sheetTable(data)
  },
}
